// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import RoutingService from "./../RoutingService";
import DataStore from "./DataStore";
const endPoint = RoutingService.getHostPath();

let latestContactQuery = { pageSize: 100 };
let latestMessageQuery = { searchText: "", startDate: null, endDate: null };
let latestCaseQuery = {};

const updateContactState = function (contactMap, newContact) {
  let newMap = {
    ...contactMap,
    contacts: contactMap.contacts.map((c) => {
      if (c.id !== newContact.id) {
        return c;
      }
      return { ...newContact };
    }),
    trashed: contactMap.trashed.map((c) => {
      if (c.id !== newContact.id) {
        return c;
      }
      return { ...newContact };
    }),
  };
  newMap = rebuildContactMap(newMap);
  return newMap;
};

const rebuildContactMap = function (oldMap) {
  var newMap = { ...oldMap };
  var contacts = oldMap.contacts.concat(oldMap.trashed);
  newMap.contacts = contacts.filter((c) => !c.trashed);
  newMap.trashed = contacts.filter((c) => c.trashed);

  //Update counts if a contact has changed type
  newMap.count += newMap.contacts.length - oldMap.contacts.length;
  newMap.trashedCount += newMap.trashed.length - oldMap.trashed.length;

  return newMap;
};

const updateMessageState = function (messageMap, newMessage) {
  let newMap = { ...messageMap };
  var currentMessage = newMap.messages.find((m) => m.id === newMessage.id);
  if (!!currentMessage) {
    Object.assign(currentMessage, newMessage);
  } else {
    newMap.messages.push(newMessage);
  }
  newMap = makeMessageMap(newMap, newMap.messages);
  return newMap;
};

const getSenderId = (message) => {
  return !!message.sender ? message.sender.id : 0;
};

const getReceiverId = (message) => {
  return !!message.receiver ? message.receiver.id : 0;
};

const makeMessageMap = (newMap, messages) => {
  let userId = DataStore.getStore().userProfile.id;
  newMap.messages = messages;
  newMap.received = messages.filter((message) => {
    return (
      getReceiverId(message) === userId &&
      !message.draft &&
      !message.trashed &&
      !message.spam &&
      !message.addedLabel
    );
  });
  newMap.spam = messages.filter((message) => {
    return getReceiverId(message) === userId && message.spam;
  });
  newMap.important = messages.filter((message) => {
    return (
      (getReceiverId(message) === userId || getSenderId(message) === userId) &&
      message.important &&
      !message.trashed
    );
  });
  newMap.sent = messages.filter((message) => {
    return (
      getSenderId(message) === userId && !message.draft && !message.trashed
    );
  });
  newMap.drafts = messages.filter((message) => {
    return getSenderId(message) === userId && message.draft && !message.trashed;
  });
  //Show received mails, drafts and mails sent to external mail servers
  newMap.trashed = messages.filter((message) => {
    return (
      (getReceiverId(message) === userId ||
        (getSenderId(message) === userId && message.draft) ||
        (getSenderId(message) === userId &&
          !!message.sender &&
          !message.receiver)) &&
      message.trashed
    );
  });

  return newMap;
};

// Define a service using a base URL and expected endpoints
export const axolexApi = createApi({
  reducerPath: "axolexApi",
  baseQuery: fetchBaseQuery({ baseUrl: `${endPoint}/api` }),
  keepUnusedDataFor: 300,
  tagTypes: ["Contacts", "Messages", "MessageCounter", "Cases", "SingleCase", "RealEstateProperties"],
  endpoints: (builder) => ({
    //Contact endpoints
    getContacts: builder.query({
      query: (contactQueryParams) => {
        latestContactQuery = contactQueryParams; //For optimistic updating
        let { pageSize, ...queryString } = contactQueryParams;
        return {
          url: `ContactInfoes/all/${pageSize}`,
          params: queryString,
          credentials: "include",
        };
      },
      providesTags: (result) =>
        result // successful query
          ? [
              ...result.contacts.map(({ id }) => ({ type: "Contacts", id })),
              { type: "Contacts", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Contacts', id: 'LIST' }` is invalidated
            [{ type: "Contacts", id: "LIST" }],
    }),
    refetchContacts: builder.mutation({
      queryFn: () => ({ data: null }),
      invalidatesTags: ["Contacts"],
    }),
    getSingleContact: builder.query({
      query: (id) => {
        return {
          url: `ContactInfoes/single/${id}`,
          credentials: "include",
        };
      },
      providesTags: (result, error, id) => [{ type: "Contacts", id }],
    }),
    createContact: builder.mutation({
      query(newContact) {
        return {
          url: `ContactInfoes/add`,
          method: "POST",
          body: newContact,
          credentials: "include",
        };
      },
      // async onQueryStarted(newContact, { dispatch, queryFulfilled }) { //Optimistic updating
      //   const patchResultList = dispatch(
      //     axolexApi.util.updateQueryData('getContacts', latestContactQuery, (draft) => {
      //       Object.assign(draft, {
      //         ...draft,
      //         contacts: [
      //           {...newContact},
      //           ...draft.contacts,
      //         ]
      //       });
      //     }))
      //   try {
      //     await queryFulfilled;
      //   } catch {
      //     dispatch(axolexApi.util.invalidateTags(['Contacts']))
      //   }
      // },
      invalidatesTags: [{ type: "Contacts", id: "LIST" }],
    }),
    updateContact: builder.mutation({
      query: (newContact) => ({
        url: `ContactInfoes/update/${newContact.id}`,
        method: "PUT",
        body: newContact,
        credentials: "include",
      }),
      // invalidatesTags: (result, error, { id }) => [{ type: 'Contacts', id }],
      async onQueryStarted(newContact, { dispatch, queryFulfilled }) {
        //Optimistic updating
        const patchResultSingle = dispatch(
          axolexApi.util.updateQueryData(
            "getSingleContact",
            newContact.id,
            (draft) => {
              Object.assign(draft, newContact);
            }
          )
        );
        const patchResultList = dispatch(
          axolexApi.util.updateQueryData(
            "getContacts",
            latestContactQuery,
            (draft) => {
              Object.assign(draft, updateContactState(draft, newContact));
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResultSingle.undo();
          patchResultList.undo();
        }
      },
    }),
    deleteContact: builder.mutation({
      query(id) {
        return {
          url: `ContactInfoes/delete/${id}`,
          method: "DELETE",
          credentials: "include",
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        //Optimistic updating
        dispatch(
          axolexApi.util.updateQueryData(
            "getContacts",
            latestContactQuery,
            (draft) => {
              Object.assign(draft, {
                ...draft,
                trashed: draft.trashed.filter((c) => c.id !== id),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(axolexApi.util.invalidateTags(["Contacts"]));
        }
      },
    }),
    checkoutContact: builder.mutation({
      query(id) {
        return {
          url: `ContactInfoes/checkout/${id}`,
          method: "POST",
          credentials: "include",
        };
      },
      invalidatesTags: [{ type: "Contacts", id: "LIST" }],
    }),
    createClientAccount: builder.mutation({
      query(contact) {
        return {
          url: `ContactInfoes/createClientAccount/`,
          method: "POST",
          body: contact,
          credentials: "include",
        };
      },
      invalidatesTags: [{ type: "Contacts", id: "LIST" }],
    }),
    registerEInvoicing: builder.mutation({
      query(id) {
        return {
          url: `EInvoices/CreateChildCompany/${id}`,
          method: "POST",
          credentials: "include",
        };
      },
      invalidatesTags: [{ type: "Contacts", id: "LIST" }],
    }),
    updateNewEntries: builder.mutation({
      query() {
        return {
          url: `ContactInfoes/updateNewEntries/`,
          method: "POST",
          credentials: "include",
        };
      },
      invalidatesTags: [{ type: "Contacts", id: "LIST" }],
    }),

    //Email message endpoints
    getMessages: builder.query({
      query: (params) => {
        latestMessageQuery = params;
        let { pageSize, ...queryString } = params;
        let queryParams = {
          ...queryString,
          startDate: queryString.startDate
            ? queryString.startDate.format()
            : null,
          endDate: queryString.endDate ? queryString.endDate.format() : null,
        };
        return {
          url: `message/headers/${pageSize}`,
          params: queryParams,
          credentials: "include",
        };
      },
      providesTags: (result) =>
        result // successful query
          ? [
              ...result.messages.map(({ id }) => ({ type: "Messages", id })),
              { type: "Messages", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Contacts', id: 'LIST' }` is invalidated
            [{ type: "Messages", id: "LIST" }],
    }),
    refetchMessages: builder.mutation({
      queryFn: () => ({ data: null }),
      invalidatesTags: [
        { type: "Messages", id: "LIST" },
        { type: "MessageCounter" },
      ],
    }),
    getMessage: builder.query({
      query: (id) => {
        return {
          url: `message/${id}`,
          credentials: "include",
        };
      },
      providesTags: (result, error, id) => [{ type: "Messages", id }],
    }),
    getMessageHeader: builder.query({
      query: (id) => {
        return {
          url: `message/header/${id}`,
          credentials: "include",
        };
      },
      providesTags: (result, error, id) => [{ type: "Messages", id }],
    }),
    getMessageCounter: builder.query({
      query: (id) => {
        return {
          url: `message/count/`,
          credentials: "include",
        };
      },
      providesTags: (result, error, id) => [{ type: "MessageCounter" }],
    }),
    createMessage: builder.mutation({
      query(newMessage) {
        return {
          url: `message/createMessage`,
          method: "POST",
          body: newMessage,
          credentials: "include",
        };
      },
      invalidatesTags: [
        { type: "Messages", id: "LIST" },
        { type: "MessageCounter" },
      ],
    }),
    updateMessage: builder.mutation({
      query: (newMessage) => ({
        url: `message/updateMessage`,
        method: "POST",
        body: newMessage,
        credentials: "include",
      }),
      // invalidatesTags: (result, error, { id }) => [{ type: 'Contacts', id }],
      async onQueryStarted(newMessage, { dispatch, queryFulfilled }) {
        //Optimistic updating
        const patchResultSingle = dispatch(
          axolexApi.util.updateQueryData(
            "getMessage",
            newMessage.id,
            (draft) => {
              Object.assign(draft, newMessage);
            }
          )
        );
        const patchResultHeader = dispatch(
          axolexApi.util.updateQueryData(
            "getMessageHeader",
            newMessage.id,
            (draft) => {
              Object.assign(draft, newMessage);
            }
          )
        );
        const patchResultList = dispatch(
          axolexApi.util.updateQueryData(
            "getMessages",
            latestMessageQuery,
            (draft) => {
              Object.assign(draft, updateMessageState(draft, newMessage));
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResultSingle.undo();
          patchResultHeader.undo();
          patchResultList.undo();
        }
      },
      invalidatesTags: [{ type: "MessageCounter" }],
    }),
    updateMessageFromHeader: builder.mutation({
      query: (newMessage) => ({
        url: `message/updateFromHeader`,
        method: "POST",
        body: newMessage,
        credentials: "include",
      }),
      async onQueryStarted(newMessage, { dispatch, queryFulfilled }) {
        //Optimistic updating
        const patchResultSingle = dispatch(
          axolexApi.util.updateQueryData(
            "getMessage",
            newMessage.id,
            (draft) => {
              Object.assign(draft, newMessage);
            }
          )
        );
        const patchResultHeader = dispatch(
          axolexApi.util.updateQueryData(
            "getMessageHeader",
            newMessage.id,
            (draft) => {
              Object.assign(draft, newMessage);
            }
          )
        );
        const patchResultList = dispatch(
          axolexApi.util.updateQueryData(
            "getMessages",
            latestMessageQuery,
            (draft) => {
              Object.assign(draft, updateMessageState(draft, newMessage));
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResultSingle.undo();
          patchResultHeader.undo();
          patchResultList.undo();
        }
      },
      invalidatesTags: [{ type: "MessageCounter" }],
    }),
    deleteMessage: builder.mutation({
      query(id) {
        return {
          url: `message/deleteMessage/${id}`,
          method: "POST",
          credentials: "include",
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        //Optimistic updating
        dispatch(
          axolexApi.util.updateQueryData(
            "getMessages",
            latestMessageQuery,
            (draft) => {
              let newMap = makeMessageMap(
                draft,
                draft.messages.filter((m) => m.id !== id)
              );
              Object.assign(draft, newMap);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          dispatch(axolexApi.util.invalidateTags(["Messages"]));
        }
      },
      invalidatesTags: [{ type: "MessageCounter" }],
    }),

    //Case endpoints
    getCases: builder.query({
      query: (params) => {
        latestCaseQuery = params;
        let { pageSize, ...queryString } = params;
        return {
          url: `cases/headers/${pageSize}`,
          params: queryString,
          credentials: "include",
        };
      },
      providesTags: (result) =>
        result // successful query
          ? [
              ...result.cases.map(({ id }) => ({ type: "Cases", id })),
              ...result.cases.map(({ id }) => ({ type: "SingleCase", id })),
              { type: "Cases", id: "LIST" },
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Contacts', id: 'LIST' }` is invalidated
            [{ type: "Cases", id: "LIST" }],
    }),
    refetchCases: builder.mutation({
      queryFn: () => ({ data: null }),
      invalidatesTags: [{ type: "Cases", id: "LIST" }],
    }),
    updateCaseMapState: builder.mutation({
      //Used for optimistic updating
      queryFn: (newCaseMap) => {
        return { data: null };
      },
      // invalidatesTags: (result, error, { id }) => [{ type: 'Contacts', id }],
      async onQueryStarted(newCaseMap, { dispatch }) {
        //Optimistic updating
        dispatch(
          axolexApi.util.updateQueryData(
            "getCases",
            latestCaseQuery,
            (draft) => {
              Object.assign(draft, newCaseMap);
            }
          )
        );
      },
    }),
    getCaseModel: builder.query({
      query: (id) => {
        return {
          url: `cases/single//${id}`,
          credentials: "include",
        };
      },
      providesTags: (result, error, id) => [{ type: "SingleCase", id }],
    }),
    updateCaseModelState: builder.mutation({
      //Used for optimistic updating
      queryFn: (newCaseModel) => {
        return { data: null };
      },
      // invalidatesTags: (result, error, { id }) => [{ type: 'Contacts', id }],
      async onQueryStarted(newCaseModel, { dispatch }) {
        //Optimistic updating
        dispatch(
          axolexApi.util.updateQueryData(
            "getCaseModel",
            newCaseModel.id,
            (draft) => {
              Object.assign(draft, newCaseModel);
            }
          )
        );
      },
    }),
    refetchCaseModel: builder.mutation({
      queryFn: (id) => ({ data: id }),
      invalidatesTags: (result, error, arg) => [
        { type: "SingleCase", id: arg },
      ],
    }),

    getRealEstateDataFromAddress: builder.query({
      query: (params) => {
        latestCaseQuery = params;
        return {
          url: `/Tinglysning/address/`,
          params: params,
          credentials: "include",
        };
      },
      providesTags: (result) =>
        result // successful query
          ? [ { type: "RealEstateProperties", id: "LIST" }, ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Contacts', id: 'LIST' }` is invalidated
            [{ type: "RealEstateProperties", id: "LIST" }],
    }),

    getRealEstateDetails: builder.query({
      query: (uuid) => {
        return {
          url: `/Tinglysning/uuid/`,
          params: { uuid },
          credentials: "include",
          responseHandler: 'text'
        };
      },
    }),

    getCoopDataFromAddress: builder.query({
      query: (params) => {
        latestCaseQuery = params;
        return {
          url: `/Tinglysning/coopAddress/`,
          params: params,
          credentials: "include",
        };
      },
      providesTags: (result) =>
        result // successful query
          ? [ { type: "RealEstateProperties", id: "LIST" }, ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Contacts', id: 'LIST' }` is invalidated
            [{ type: "RealEstateProperties", id: "LIST" }],
    }),

    getCoopDetails: builder.query({
      query: (uuid) => {
        return {
          url: `/Tinglysning/coopUuid/`,
          params: { uuid },
          credentials: "include",
          responseHandler: 'text'
        };
      },
    }),

    getCompanyDocuments: builder.query({
      query: (cvr) => {
        return {
          url: `/Tinglysning/Company/`,
          params: { cvr },
          credentials: "include",
          responseHandler: 'text'
        };
      },
    }),

    getCVRParticipantByNameRaw: builder.query({
      query: (name) => {
        return {
          url: `/CVRDetails/raw/person/name/${name}`,
          credentials: "include",
          responseHandler: 'text'
        };
      },
    }),

    getCVRParticipantByName: builder.query({
      query: (name) => {
        return {
          url: `/CVRDetails/person/name/${name}`,
          credentials: "include",
        };
      },
    }),

    getVehicleByChassis: builder.query({
      query: (stelnummer) => {
        return {
          url: `/Tinglysning/vehicle/${stelnummer}`,
          credentials: "include",
        };
      },
    }),

    getDocumentById: builder.query({
      query: (uuid) => {
        return {
          url: `/Tinglysning/document/${uuid}`,
          credentials: "include",
          responseHandler: 'text'
        };
      },
    }),

  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetContactsQuery,
  useGetSingleContactQuery,
  useRefetchContactsMutation,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useCheckoutContactMutation,
  useCreateClientAccountMutation,
  useRegisterEInvoicingMutation,
  useUpdateNewEntriesMutation,
  useGetMessagesQuery,
  useRefetchMessagesMutation,
  useGetMessageQuery,
  useGetMessageHeaderQuery,
  useGetMessageCounterQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation,
  useUpdateMessageFromHeaderMutation,
  useDeleteMessageMutation,
  useGetCasesQuery,
  useGetCaseModelQuery,
  useRefetchCasesMutation,
  useRefetchCaseModelMutation,
  useUpdateCaseMapStateMutation,
  useUpdateCaseModelStateMutation,
  useGetRealEstateDataFromAddressQuery,
  useGetRealEstateDetailsQuery,
  useGetCoopDataFromAddressQuery,
  useGetCoopDetailsQuery,
  useGetCompanyDocumentsQuery,
  useGetCVRParticipantByNameQuery,
  useGetVehicleByChassisQuery,
  useGetDocumentByIdQuery
} = axolexApi;
